import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { intervalLabel, type AgreementBilling, type RepeatIntervalUnit } from '@fieldops/shared'
import { useState, type ReactNode } from 'react'
import { addressLine } from '../../lib/format.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { AddressField, addressDraftFrom, addressIsUsable, type AddressDraft } from '../AddressField.js'
import { Icon } from '../Icon.js'
import { Modal, ModalCloseButton } from '../Modal.js'
import { PriceInput } from '../proposal/PricingInspector.js'
import { Button, ErrorState, Field, Input, Select, Textarea, Toggle } from '../ui.js'
import type { AgreementPage } from './AgreementGrid.js'
import { commonRule, siteTitle, type AgreementGroup } from './agreement-view.js'
import { BILLING_LABEL, FREQUENCIES } from './builder-model.js'
import { TickBox, tickedOf } from './review-parts.js'

type Status = 'ACTIVE' | 'PAUSED' | 'ENDED'
type Editable = Pick<
  AgreementPage,
  'id' | 'name' | 'status' | 'startsOn' | 'endsOn' | 'notes' | 'billing' | 'autoRenew'
>

/**
 * An agreement's name, term, billing and status. Ending one stops every
 * schedule in it; pausing holds their jobs. A new agreement is the builder's.
 */
export function AgreementPopup({
  open,
  onClose,
  agreement,
}: {
  open: boolean
  onClose: () => void
  agreement: Editable
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="agreement-popup-title" className="max-w-[520px]">
      {open ? <AgreementForm agreement={agreement} onClose={onClose} /> : null}
    </Modal>
  )
}

function AgreementForm({ agreement, onClose }: { agreement: Editable; onClose: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [name, setName] = useState(agreement.name)
  const [startsOn, setStartsOn] = useState(agreement.startsOn)
  const [endsOn, setEndsOn] = useState(agreement.endsOn ?? '')
  const [status, setStatus] = useState<Status>(agreement.status)
  const [billing, setBilling] = useState<AgreementBilling>(agreement.billing)
  const [autoRenew, setAutoRenew] = useState(agreement.autoRenew)
  const [notes, setNotes] = useState(agreement.notes ?? '')

  const update = useMutation(
    trpc.agreements.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.agreements.pathFilter())
        void queryClient.invalidateQueries(trpc.repeat.pathFilter())
        onClose()
      },
    }),
  )
  const problem = !name.trim()
    ? 'Name the agreement.'
    : endsOn && endsOn < startsOn
      ? 'The term has to end after it starts.'
      : null

  const save = () => {
    if (problem) return
    update.mutate({
      id: agreement.id,
      name: name.trim(),
      startsOn,
      endsOn: endsOn || null,
      notes: notes.trim() || null,
      status,
      billing,
      autoRenew,
    })
  }

  return (
    <>
      <div className="flex items-center gap-3 border-b border-ink-200 px-5 py-3.5">
        <h2 id="agreement-popup-title" className="text-base font-semibold text-ink-900">
          Edit agreement
        </h2>
        <div className="ml-auto">
          <ModalCloseButton onClose={onClose} />
        </div>
      </div>
      <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Name" htmlFor="agreement-name">
            <Input id="agreement-name" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
        </div>
        <Field label="Term starts" htmlFor="agreement-starts">
          <Input
            id="agreement-starts"
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
          />
        </Field>
        <Field label="Term ends" htmlFor="agreement-ends" hint="Leave empty for no end.">
          <Input
            id="agreement-ends"
            type="date"
            value={endsOn}
            onChange={(event) => setEndsOn(event.target.value)}
          />
        </Field>
        <Field label="Billing" htmlFor="agreement-billing" hint="Recorded only. Nothing invoices from it yet.">
          <Select
            id="agreement-billing"
            value={billing}
            onChange={(event) => setBilling(event.target.value as AgreementBilling)}
          >
            {(Object.keys(BILLING_LABEL) as AgreementBilling[]).map((value) => (
              <option key={value} value={value}>
                {BILLING_LABEL[value]}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-center gap-2 self-end pb-1">
          <Toggle checked={autoRenew} onChange={setAutoRenew} label="Renew automatically" />
          <span className="text-[13px] text-ink-800">Renew automatically</span>
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Status"
            htmlFor="agreement-status"
            hint={
              status === 'ENDED'
                ? 'Every schedule in it stops. Jobs already made stay.'
                : status === 'PAUSED'
                  ? 'No new jobs are made until it is active again.'
                  : undefined
            }
          >
            <Select
              id="agreement-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as Status)}
            >
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="ENDED">Ended</option>
            </Select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Notes" htmlFor="agreement-notes">
            <Textarea
              id="agreement-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
            />
          </Field>
        </div>
        {update.error ? (
          <div className="sm:col-span-2">
            <ErrorState message={errorMessage(update.error)} />
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-3 border-t border-ink-200 px-5 py-3.5">
        <p className="min-w-0 flex-1 text-[12.5px] text-ink-600">{problem}</p>
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={problem !== null} loading={update.isPending} onClick={save}>
          Save agreement
        </Button>
      </div>
    </>
  )
}

type ServiceRow = AgreementPage['schedules'][number]
type SiteRef = AgreementPage['clientSites'][number] | ServiceRow['site']
type Line = { key: string; serviceTypeId: string; priceIncTax: number | null }

const frequencyKey = (interval: { intervalCount: number; intervalUnit: RepeatIntervalUnit }) =>
  `${interval.intervalCount}-${interval.intervalUnit}`

/** Everything the agreement page changes, and everything drawn from it. */
export function useInvalidateAgreement() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries(trpc.agreements.pathFilter())
    void queryClient.invalidateQueries(trpc.repeat.pathFilter())
  }
}

function PopupHeader({ id, title, sub, onClose }: { id: string; title: string; sub?: string; onClose: () => void }) {
  return (
    <div className="flex items-start gap-3 border-b border-ink-200 px-5 py-3.5">
      <div className="min-w-0 flex-1">
        <h2 id={id} className="text-base font-semibold text-ink-900">
          {title}
        </h2>
        {sub ? <p className="truncate text-xs text-ink-500">{sub}</p> : null}
      </div>
      <ModalCloseButton onClose={onClose} />
    </div>
  )
}

/**
 * The red strip that stands between a remove button and the removal. Jobs
 * already made are never touched, and it says so.
 */
function ConfirmRemove({
  children,
  loading,
  onKeep,
  onRemove,
}: {
  children: ReactNode
  loading: boolean
  onKeep: () => void
  onRemove: () => void
}) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 border-t border-red-200 bg-red-50 px-5 py-3">
      <p className="min-w-0 flex-1 text-[12.5px] text-red-800">{children}</p>
      <Button size="sm" onClick={onKeep}>
        Keep it
      </Button>
      <Button size="sm" variant="danger" loading={loading} onClick={onRemove}>
        Remove
      </Button>
    </div>
  )
}

/**
 * Edit service: what is done on the visit, what it costs, how often and when
 * the first visit falls. Pause and remove are here too. Decision 96.
 */
export function ServicePopup({
  agreementId,
  row,
  term,
  onClose,
}: {
  agreementId: string
  row: ServiceRow | null
  term: { startsOn: string; endsOn: string }
  onClose: () => void
}) {
  return (
    <Modal open={row !== null} onClose={onClose} labelledBy="service-popup-title" className="max-w-[560px]">
      {row ? <ServiceForm agreementId={agreementId} row={row} term={term} onClose={onClose} /> : null}
    </Modal>
  )
}

function ServiceForm({
  agreementId,
  row,
  term,
  onClose,
}: {
  agreementId: string
  row: ServiceRow
  term: { startsOn: string; endsOn: string }
  onClose: () => void
}) {
  const trpc = useTRPC()
  const invalidate = useInvalidateAgreement()
  const catalogue = useQuery(trpc.forms.serviceTypes.queryOptions())

  const [lines, setLines] = useState<Line[]>(() =>
    row.services.map((service, index) => ({ key: `${index}`, serviceTypeId: service.serviceTypeId, priceIncTax: service.priceIncTax })),
  )
  const [frequency, setFrequency] = useState(frequencyKey(row))
  const [firstDueOn, setFirstDueOn] = useState(row.firstDueOn)
  const [confirming, setConfirming] = useState(false)

  const done = () => {
    invalidate()
    onClose()
  }
  const save = useMutation(trpc.agreements.updateService.mutationOptions({ onSuccess: done }))
  const pause = useMutation(trpc.agreements.setServicePaused.mutationOptions({ onSuccess: done }))
  const remove = useMutation(trpc.agreements.removeService.mutationOptions({ onSuccess: done }))
  const failure = save.error ?? pause.error ?? remove.error

  // A retired service is off the catalogue and still on the schedule, and a
  // select whose value matches no option would save the first one instead.
  const types = (catalogue.data ?? []).map(({ id, name, defaultPriceIncTax }) => ({ id, name, defaultPriceIncTax }))
  for (const service of row.services) {
    if (!types.some((type) => type.id === service.serviceTypeId)) {
      types.push({ id: service.serviceTypeId, name: service.name, defaultPriceIncTax: null })
    }
  }
  const frequencies = FREQUENCIES.some((option) => frequencyKey(option) === frequencyKey(row))
    ? FREQUENCIES
    : [...FREQUENCIES, { intervalCount: row.intervalCount, intervalUnit: row.intervalUnit }]

  const stopped = row.stoppedAt !== null
  const name = row.services.map((service) => service.name).join(', ') || 'this service'
  const site = row.site?.name ?? addressLine(row.site?.address)
  const problem = lines.some((line) => !line.serviceTypeId)
    ? 'Pick a service.'
    : !firstDueOn
      ? 'Pick the first visit.'
      : null

  const setLine = (key: string, change: Partial<Line>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)))

  const submit = () => {
    if (problem) return
    const [count, unit] = frequency.split('-') as [string, RepeatIntervalUnit]
    save.mutate({
      agreementId,
      scheduleId: row.id,
      intervalCount: Number(count),
      intervalUnit: unit,
      firstDueOn,
      services: lines.map((line) => ({ serviceTypeId: line.serviceTypeId, priceIncTax: line.priceIncTax })),
    })
  }

  return (
    <>
      <PopupHeader id="service-popup-title" title="Edit service" sub={site} onClose={onClose} />
      <div className="flex flex-col gap-4 px-5 py-5">
        <div className="flex flex-col gap-2">
          {lines.map((line, index) => (
            <div key={line.key} className="grid grid-cols-[1fr_120px_auto] items-end gap-2">
              <Field label={index === 0 ? 'Service' : 'And'} htmlFor={`service-type-${line.key}`}>
                <Select
                  id={`service-type-${line.key}`}
                  value={line.serviceTypeId}
                  onChange={(event) => {
                    const type = types.find((candidate) => candidate.id === event.target.value)
                    setLine(line.key, {
                      serviceTypeId: event.target.value,
                      ...(type?.defaultPriceIncTax != null && line.priceIncTax === null
                        ? { priceIncTax: type.defaultPriceIncTax }
                        : {}),
                    })
                  }}
                >
                  {line.serviceTypeId ? null : <option value="">Pick a service</option>}
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Per visit" htmlFor={`service-price-${line.key}`}>
                <PriceInput
                  id={`service-price-${line.key}`}
                  value={line.priceIncTax}
                  onChange={(cents) => setLine(line.key, { priceIncTax: cents })}
                  placeholder="$0.00"
                />
              </Field>
              {lines.length > 1 ? (
                <button
                  type="button"
                  aria-label={`Take ${types.find((type) => type.id === line.serviceTypeId)?.name ?? 'this service'} off the visit`}
                  onClick={() => setLines((current) => current.filter((candidate) => candidate.key !== line.key))}
                  className="mb-1.5 rounded-md p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                >
                  <Icon name="close" className="size-4" />
                </button>
              ) : (
                <span className="w-7" />
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setLines((current) => [...current, { key: `${Date.now()}`, serviceTypeId: '', priceIncTax: null }])}
            className="inline-flex w-fit items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-50"
          >
            <Icon name="plus" className="size-3.5" />
            Another service on the same visit
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="How often" htmlFor="service-often">
            <Select id="service-often" value={frequency} onChange={(event) => setFrequency(event.target.value)}>
              {frequencies.map((option) => (
                <option key={frequencyKey(option)} value={frequencyKey(option)}>
                  {intervalLabel(option)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="First visit" htmlFor="service-first">
            <Input
              id="service-first"
              type="date"
              min={term.startsOn}
              max={term.endsOn}
              value={firstDueOn}
              onChange={(event) => setFirstDueOn(event.target.value)}
            />
          </Field>
        </div>
        <p className="text-xs text-ink-500">
          Visits already made into jobs keep their jobs. The planned ones move to the new dates.
        </p>

        {failure ? <ErrorState message={errorMessage(failure)} /> : null}
      </div>

      {confirming ? (
        <ConfirmRemove loading={remove.isPending} onKeep={() => setConfirming(false)} onRemove={() => remove.mutate({ agreementId, scheduleId: row.id })}>
          {`Take ${name} off the agreement? Nothing more is planned for it. Jobs already made stay.`}
        </ConfirmRemove>
      ) : (
        <div className="flex flex-wrap items-center gap-2 border-t border-ink-200 px-5 py-3.5">
          <Button
            size="sm"
            variant="ghost"
            icon={<Icon name={stopped ? 'play' : 'pause'} className="size-3.5" />}
            loading={pause.isPending}
            onClick={() => pause.mutate({ agreementId, scheduleId: row.id, paused: !stopped })}
          >
            {stopped ? 'Resume service' : 'Pause service'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-red-700 hover:bg-red-50"
            icon={<Icon name="trash" className="size-3.5" />}
            onClick={() => setConfirming(true)}
          >
            Remove
          </Button>
          <p className="min-w-0 flex-1 text-right text-[12.5px] text-ink-600">{problem}</p>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={problem !== null} loading={save.isPending} onClick={submit}>
            Save service
          </Button>
        </div>
      )}
    </>
  )
}

/**
 * Edit site: the property's own name and address, and the site's place on
 * the agreement — pause everything there, or take it off. Removing takes the
 * site off this agreement only; the property stays the client's.
 */
export function SitePopup({
  agreementId,
  site,
  services,
  paused,
  onClose,
}: {
  agreementId: string
  site: SiteRef | null
  services: number
  paused: boolean
  onClose: () => void
}) {
  return (
    <Modal open={site !== null} onClose={onClose} labelledBy="site-popup-title" className="max-w-[560px]">
      {site ? (
        <SiteForm agreementId={agreementId} site={site} services={services} paused={paused} onClose={onClose} />
      ) : null}
    </Modal>
  )
}

function SiteForm({
  agreementId,
  site,
  services,
  paused,
  onClose,
}: {
  agreementId: string
  site: SiteRef
  services: number
  paused: boolean
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const invalidate = useInvalidateAgreement()

  const [original] = useState(() => addressDraftFrom(site.address))
  const [address, setAddress] = useState<AddressDraft>(original)
  const [name, setName] = useState(site.name ?? '')
  const [confirming, setConfirming] = useState(false)

  const done = () => {
    invalidate()
    onClose()
  }
  const save = useMutation(
    trpc.clients.updateSite.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
        done()
      },
    }),
  )
  const pause = useMutation(trpc.agreements.setSitePaused.mutationOptions({ onSuccess: done }))
  const remove = useMutation(trpc.agreements.removeSite.mutationOptions({ onSuccess: done }))
  const failure = save.error ?? pause.error ?? remove.error

  const title = site.name ?? addressLine(site.address)
  const addressChanged = JSON.stringify(address) !== JSON.stringify(original)
  const nameChanged = (name.trim() || null) !== (site.name ?? null)
  const problem = addressChanged && !addressIsUsable(address) ? 'The address needs a street and a suburb.' : null

  const submit = () => {
    if (problem) return
    if (!addressChanged && !nameChanged) return onClose()
    save.mutate({
      id: site.id,
      ...(nameChanged ? { name: name.trim() || null } : {}),
      // Only a changed address is sent, so renaming cannot touch the location.
      ...(addressChanged ? { address: { ...address, raw: address.raw ?? addressLine(address) } } : {}),
    })
  }

  return (
    <>
      <PopupHeader
        id="site-popup-title"
        title="Edit site"
        sub={`${title} · ${services} service${services === 1 ? '' : 's'} on this agreement`}
        onClose={onClose}
      />
      <div className="flex flex-col gap-4 px-5 py-5">
        <Field label="Address" htmlFor="site-edit-address">
          <AddressField id="site-edit-address" value={address} onChange={setAddress} />
        </Field>
        <Field label="Name it (optional)" htmlFor="site-edit-name">
          <Input id="site-edit-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Warehouse 3" />
        </Field>
        <p className="text-xs text-ink-500">The address and name change for this property everywhere, on every job there too.</p>
        {failure ? <ErrorState message={errorMessage(failure)} /> : null}
      </div>

      {confirming ? (
        <ConfirmRemove loading={remove.isPending} onKeep={() => setConfirming(false)} onRemove={() => remove.mutate({ agreementId, siteId: site.id })}>
          {`Take ${title} off the agreement, with its ${services} service${services === 1 ? '' : 's'}? The site stays the client’s, and jobs already made stay.`}
        </ConfirmRemove>
      ) : (
        <div className="flex flex-wrap items-center gap-2 border-t border-ink-200 px-5 py-3.5">
          <Button
            size="sm"
            variant="ghost"
            icon={<Icon name={paused ? 'play' : 'pause'} className="size-3.5" />}
            loading={pause.isPending}
            onClick={() => pause.mutate({ agreementId, siteId: site.id, paused: !paused })}
          >
            {paused ? 'Resume site' : 'Pause site'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-red-700 hover:bg-red-50"
            icon={<Icon name="trash" className="size-3.5" />}
            onClick={() => setConfirming(true)}
          >
            Remove from agreement
          </Button>
          <p className="min-w-0 flex-1 text-right text-[12.5px] text-ink-600">{problem}</p>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={problem !== null} loading={save.isPending} onClick={submit}>
            Save site
          </Button>
        </div>
      )}
    </>
  )
}

/** As many sites as `agreements.build` takes in one go. */
const MOST_SITES = 100

/**
 * Add sites to a service: the client's sites that do not have it yet, ticked,
 * each getting the service on the rule most of its sites already have. One
 * `agreements.build`, so they all go on or none does. Decision 96.
 */
export function AddSitesPopup({
  page,
  group,
  onClose,
  onAdded,
}: {
  page: AgreementPage
  group: AgreementGroup | null
  onClose: () => void
  onAdded: (message: string) => void
}) {
  return (
    <Modal open={group !== null} onClose={onClose} labelledBy="add-sites-title" className="max-w-[620px]">
      {group ? <AddSitesForm page={page} group={group} onClose={onClose} onAdded={onAdded} /> : null}
    </Modal>
  )
}

function AddSitesForm({
  page,
  group,
  onClose,
  onAdded,
}: {
  page: AgreementPage
  group: AgreementGroup
  onClose: () => void
  onAdded: (message: string) => void
}) {
  const trpc = useTRPC()
  const invalidate = useInvalidateAgreement()
  const serviceTypeId = group.serviceTypeId!
  const common = commonRule(group.rows)

  const [find, setFind] = useState('')
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
  const [frequency, setFrequency] = useState(frequencyKey(common))
  const [priceIncTax, setPriceIncTax] = useState<number | null>(common.priceIncTax)
  const [firstDueOn, setFirstDueOn] = useState(common.firstDueOn ?? page.startsOn)

  const having = new Set(
    page.schedules.filter((row) => row.services.some((service) => service.serviceTypeId === serviceTypeId)).map((row) => row.siteId),
  )
  const candidates = page.clientSites
    .filter((site) => !having.has(site.id))
    .map((site) => ({ id: site.id, ...siteTitle(site, page.client.id) }))
    .sort((a, b) => a.name.localeCompare(b.name))
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const shown = candidates.filter((site) => {
    const text = `${site.name} ${site.address ?? ''}`.toLowerCase()
    return words.every((word) => text.includes(word))
  })
  const shownIds = shown.map((site) => site.id)

  const build = useMutation(
    trpc.agreements.build.mutationOptions({
      onSuccess: (result) => {
        invalidate()
        const made = result.created.length
        onAdded(
          [
            `${group.name} added at ${plural(picked.size, 'site')}.`,
            made ? `${plural(made, 'job')} already on the schedule.` : null,
          ]
            .filter(Boolean)
            .join(' '),
        )
        onClose()
      },
    }),
  )

  const frequencies = FREQUENCIES.some((option) => frequencyKey(option) === frequency)
    ? FREQUENCIES
    : [...FREQUENCIES, { intervalCount: common.intervalCount, intervalUnit: common.intervalUnit }]
  const problem =
    picked.size === 0
      ? 'Tick the sites to add.'
      : picked.size > MOST_SITES
        ? `Add at most ${MOST_SITES} sites at a time.`
        : !firstDueOn
          ? 'Pick the first visit.'
          : null

  const toggle = (ids: readonly string[], on: boolean) =>
    setPicked((current) => {
      const next = new Set(current)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })

  const submit = () => {
    if (problem) return
    const [count, unit] = frequency.split('-') as [string, RepeatIntervalUnit]
    build.mutate({
      agreement: { kind: 'existing', id: page.id },
      jobId: null,
      sites: [...picked].map((siteId) => ({
        siteId,
        services: [{ serviceTypeId, intervalCount: Number(count), intervalUnit: unit, firstDueOn, priceIncTax }],
      })),
    })
  }

  return (
    <>
      <PopupHeader
        id="add-sites-title"
        title={`Add sites to ${group.name}`}
        sub={`${plural(having.size, 'site')} on this agreement already have it.`}
        onClose={onClose}
      />
      <div className="flex flex-col gap-4 px-5 py-5">
        {candidates.length === 0 ? (
          <p className="text-[13px] text-ink-600">{`Every site of ${page.client.name} already has ${group.name}.`}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <Input
                type="search"
                aria-label="Find a site"
                placeholder="Find a site"
                value={find}
                onChange={(event) => setFind(event.target.value)}
                className="flex-1"
              />
              {shown.length > 0 ? (
                <label className="flex items-center gap-2 text-[12.5px] whitespace-nowrap text-ink-700">
                  <TickBox
                    checked={tickedOf(shownIds, picked)}
                    onChange={(on) => toggle(shownIds, on)}
                    label={words.length ? 'Tick every site found' : 'Tick every site'}
                  />
                  {words.length ? `All ${shown.length} found` : `All ${shown.length}`}
                </label>
              ) : null}
            </div>
            <ul aria-label="Sites without it" className="max-h-[300px] overflow-y-auto rounded-xl ring-1 ring-ink-200 ring-inset">
              {shown.map((site) => (
                <li key={site.id} className="border-b border-ink-100 last:border-b-0">
                  <label className="flex cursor-pointer items-center gap-3 px-3.5 py-2 hover:bg-ink-50">
                    <TickBox checked={picked.has(site.id)} onChange={(on) => toggle([site.id], on)} label={site.name} />
                    <span className="flex min-w-0 items-baseline gap-2">
                      <span className="truncate text-[13px] font-medium text-ink-900">{site.name}</span>
                      {site.address ? <span className="truncate text-xs text-ink-500">{site.address}</span> : null}
                    </span>
                  </label>
                </li>
              ))}
              {shown.length === 0 ? <li className="px-3.5 py-3 text-[12.5px] text-ink-500">No site matches.</li> : null}
            </ul>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="How often" htmlFor="add-sites-often">
            <Select id="add-sites-often" value={frequency} onChange={(event) => setFrequency(event.target.value)}>
              {frequencies.map((option) => (
                <option key={frequencyKey(option)} value={frequencyKey(option)}>
                  {intervalLabel(option)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Per visit" htmlFor="add-sites-price">
            <PriceInput id="add-sites-price" value={priceIncTax} onChange={setPriceIncTax} placeholder="$0.00" />
          </Field>
          <Field label="First visit" htmlFor="add-sites-first">
            <Input
              id="add-sites-first"
              type="date"
              min={page.startsOn}
              max={page.termEndsOn}
              value={firstDueOn}
              onChange={(event) => setFirstDueOn(event.target.value)}
            />
          </Field>
        </div>
        <p className="text-xs text-ink-500">Filled in from what most of its sites have. A new property is added with Add site.</p>
        {build.error ? <ErrorState message={errorMessage(build.error)} /> : null}
      </div>
      <div className="flex items-center gap-3 border-t border-ink-200 px-5 py-3.5">
        <p className="min-w-0 flex-1 text-[12.5px] text-ink-600">{problem}</p>
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={problem !== null} loading={build.isPending} onClick={submit}>
          {picked.size > 0 ? `Add to ${plural(picked.size, 'site')}` : 'Add sites'}
        </Button>
      </div>
    </>
  )
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
